import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, RefreshCw, Save } from 'lucide-react';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useProfile } from '@/features/auth/auth.store';
import type { Business } from '@/features/auth/types';
import { SlotRange, StatusBadge } from '@/features/orders/order-cells';
import { formatCurrency, formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useDeliveryProviders,
  useDeliveryZones,
  useSaveRoute,
  useZoneRoute,
  type DeliveryOrder,
} from './deliveries.api';
import { Contents, DriverBadge } from './deliveries-search';

/**
 * A zone's open deliveries, dragged into the order the driver will run them.
 *
 * Nothing is saved until Save: a route is rearranged as a whole, and writing
 * every intermediate drop would hand the driver a half-sorted list. The
 * original had the same Update button but gave no sign of whether the list on
 * screen had been saved.
 */
export function DeliveriesSorting({ business }: { business: Business }) {
  const { t } = useTranslation();
  const profile = useProfile();
  const locationId = business.locations?.[0]?.id ?? null;

  const [providerChoice, setProviderChoice] = useState<string | null>(null);
  const [zoneChoice, setZoneChoice] = useState<string | null>(null);
  /** The dragged order, as ids; `null` while the list is as the server has it. */
  const [arranged, setArranged] = useState<string[] | null>(null);

  const providers = useDeliveryProviders(profile?.id ?? null);
  const providerId = providerChoice ?? providers.data?.[0]?.id ?? '';
  const zones = useDeliveryZones(locationId, providerId, !providers.isLoading);
  // A route is always one zone's, so the first zone stands until one is picked.
  const zoneId =
    zones.data?.find((zone) => zone.id === zoneChoice)?.id ??
    zones.data?.[0]?.id ??
    '';

  const route = useZoneRoute(zoneId);
  const save = useSaveRoute();

  const stops = useMemo(() => {
    const orders = route.data ?? [];
    if (!arranged) return orders;
    const byId = new Map(orders.map((order) => [order.id, order]));
    const placed = arranged.flatMap((id) => byId.get(id) ?? []);
    // An order that arrived since the drag goes to the end rather than vanish.
    const late = orders.filter((order) => !arranged.includes(order.id));
    return [...placed, ...late];
  }, [route.data, arranged]);

  const sensors = useSensors(
    // A few pixels of travel before a drag starts, so a tap stays a tap.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadingZones = providers.isLoading || zones.isLoading;
  const loading = loadingZones || route.isLoading;
  const working = route.isFetching || save.isPending;
  const busy = working && !loading;
  const dirty = arranged !== null;

  function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const ids = stops.map((order) => order.id);
    setArranged(
      arrayMove(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id)))
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        {/* One provider is not a choice. */}
        {(providers.data?.length ?? 0) > 1 && (
          <div className="w-full space-y-1 sm:w-56">
            <Label
              htmlFor="route-provider"
              className="text-muted-foreground text-xs"
            >
              {t('deliveries.provider')}
            </Label>
            <Select
              value={providerId}
              disabled={working}
              onValueChange={(value) => {
                setProviderChoice(value);
                setZoneChoice(null);
                setArranged(null);
              }}
            >
              <SelectTrigger id="route-provider" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {providers.data?.map((provider) => (
                  <SelectItem key={provider.id} value={provider.id}>
                    {provider.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="w-full space-y-1 sm:w-56">
          <Label htmlFor="route-zone" className="text-muted-foreground text-xs">
            {t('deliveries.zone')}
          </Label>
          {loadingZones ? (
            <Skeleton className="h-9 w-full" />
          ) : (
            <Select
              value={zoneId}
              disabled={working || !zones.data?.length}
              onValueChange={(value) => {
                setZoneChoice(value);
                setArranged(null);
              }}
            >
              <SelectTrigger id="route-zone" className="w-full">
                <SelectValue placeholder={t('deliveries.noZones')} />
              </SelectTrigger>
              <SelectContent>
                {zones.data?.map((zone) => (
                  <SelectItem key={zone.id} value={zone.id}>
                    {zone.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="flex flex-1 items-center justify-end gap-2">
          {dirty && (
            <span className="text-muted-foreground text-xs">
              {t('deliveries.unsavedRoute')}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            disabled={working || !zoneId}
            onClick={() => {
              setArranged(null);
              void route.refetch();
            }}
          >
            <RefreshCw className={cn(route.isFetching && 'animate-spin')} />
            <span className="hidden sm:inline">{t('common.reload')}</span>
          </Button>
          <Button
            size="sm"
            disabled={!dirty || working}
            onClick={() =>
              save.mutate(
                stops.map((order) => order.id),
                {
                  onSuccess: () => {
                    setArranged(null);
                    toast.success(t('deliveries.routeSaved'));
                  },
                  onError: (error) =>
                    toast.error(
                      error instanceof Error && error.message
                        ? error.message
                        : t('deliveries.routeFailed')
                    ),
                }
              )
            }
          >
            <Save />
            {t('deliveries.saveRoute')}
          </Button>
        </div>
      </div>

      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-16 rounded-xl" />
          ))}
        </div>
      )}

      {route.isError && (
        <Empty>
          {route.error instanceof Error && route.error.message
            ? route.error.message
            : t('deliveries.loadFailed')}
        </Empty>
      )}

      {!loading && !zoneId && <Empty>{t('deliveries.noZones')}</Empty>}

      {route.isSuccess && stops.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={busy} />
          <Empty>{t('deliveries.noStops')}</Empty>
        </div>
      )}

      {stops.length > 0 && (
        <div className="relative">
          <BusyOverlay show={busy} />
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={stops.map((order) => order.id)}
              strategy={verticalListSortingStrategy}
              disabled={working}
            >
              <ol
                aria-busy={busy}
                className={cn(
                  'space-y-2',
                  busy && 'opacity-60 transition-opacity'
                )}
              >
                {stops.map((order, index) => (
                  <Stop key={order.id} order={order} position={index + 1} />
                ))}
              </ol>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}

function Stop({
  order,
  position,
}: {
  order: DeliveryOrder;
  position: number;
}) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: order.id });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        'bg-card flex items-center gap-3 rounded-xl border p-3',
        isDragging && 'relative z-10 shadow-lg'
      )}
    >
      <button
        type="button"
        aria-label={t('deliveries.moveStop', {
          name: order.customerName ?? order.confirmationCode ?? '',
        })}
        // `touch-none`, or a finger drag scrolls the page instead of the row.
        className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 -m-1 cursor-grab touch-none rounded p-1 outline-none focus-visible:ring-2 active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" />
      </button>

      <span className="bg-muted flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums">
        {position}
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          {order.customerName || '—'}
          {order.confirmationCode && (
            <span className="text-muted-foreground ml-2 font-mono text-xs font-normal">
              {order.confirmationCode}
            </span>
          )}
        </p>
        <p className="text-muted-foreground truncate text-xs">
          {[order.customerLocation?.addressLine, order.customerLocation?.city]
            .filter(Boolean)
            .join(', ') || '—'}
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          <StatusBadge status={order.status} />
          <DriverBadge name={order.driverName} />
          <span className="text-muted-foreground text-xs">
            {formatDate(order.desiredDateTime)}, <SlotRange order={order} />
          </span>
        </div>
      </div>

      <div className="shrink-0 text-right">
        <p className="text-sm font-medium tabular-nums">
          {formatCurrency(order.orderTotal)}
        </p>
        <p className="text-muted-foreground text-xs tabular-nums">
          <Contents order={order} />
        </p>
      </div>
    </li>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
      {children}
    </p>
  );
}
